import { Check, Pencil, RefreshCw, Undo2, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
	OutlinedPrimaryButton,
	OutlinedTextBlackButton,
	PrimaryButton,
} from "src/components/Default/LekUI/Button";
import { useToggleAiResponseApproval } from "src/hooks/useAi";
import { isStaff } from "src/hooks/useUser";

interface AiApprovalActionsProps {
	responseId: number;
	questionId?: number;
	isApproved?: boolean;
	isEditMode?: boolean;
	onEdit?: () => void;
	onCancelEdit?: () => void;
	onRegenerate?: () => void;
	/** Po udanym przelaczeniu akceptacji. */
	onApprovalToggled?: () => void;
}

export function AiApprovalActions({
	responseId,
	questionId,
	isApproved,
	isEditMode,
	onEdit,
	onCancelEdit,
	onRegenerate,
	onApprovalToggled,
}: AiApprovalActionsProps) {
	const { t } = useTranslation();
	const { mutateAsync } = useToggleAiResponseApproval();
	const [isToggling, setIsToggling] = useState(false);

	if (!isStaff()) {
		return null;
	}

	const handleToggle = async () => {
		setIsToggling(true);
		try {
			await mutateAsync({ id: responseId, questionId });
			onApprovalToggled?.();
		} catch {
			toast.error(t("errors.somethingWentWrong"), {
				position: toast.POSITION.BOTTOM_LEFT,
				autoClose: 5000,
			});
		} finally {
			setIsToggling(false);
		}
	};

	return (
		<div className="flex items-center gap-8">
			{onEdit &&
				(isEditMode ? (
					<OutlinedTextBlackButton
						onClick={onCancelEdit}
						endIcon={<X className="icon-small" />}
					>
						{t("ai.edit.cancelEdit", "Anuluj edycję")}
					</OutlinedTextBlackButton>
				) : (
					<OutlinedTextBlackButton
						onClick={onEdit}
						endIcon={<Pencil className="icon-small" />}
					>
						{t("ai.edit.button", "Edytuj")}
					</OutlinedTextBlackButton>
				))}
			{onRegenerate && (
				<OutlinedTextBlackButton
					onClick={onRegenerate}
					endIcon={<RefreshCw className="icon-small" />}
				>
					{t("ai.regenerate.button", "Przegeneruj omówienie")}
				</OutlinedTextBlackButton>
			)}
			{isApproved ? (
				<OutlinedPrimaryButton
					onClick={handleToggle}
					isLoading={isToggling}
					endIcon={<Undo2 className="icon-small" />}
				>
					{t("ai.approval.revoke", "Cofnij akceptację")}
				</OutlinedPrimaryButton>
			) : (
				<PrimaryButton
					onClick={handleToggle}
					isLoading={isToggling}
					endIcon={<Check className="icon-small" />}
				>
					{t("ai.approval.approve", "Zaakceptuj omówienie")}
				</PrimaryButton>
			)}
		</div>
	);
}
